import { useNavigate } from '@tanstack/react-router'
import { History } from 'lucide-react'
import { useState } from 'react'
import { DayList } from '@/components/timesheet/DayList'
import { DayView } from '@/components/timesheet/DayView'
import { WeekGrid } from '@/components/timesheet/WeekGrid'
import { WeekHistoryDrawer } from '@/components/timesheet/WeekHistoryDrawer'
import { useTimesheetRows } from '@/components/timesheet/useTimesheetRows'
import { PageHeader } from '@/components/ui/PageHeader'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { useCanReadFinance } from '@/lib/auth'
import { useMe, useWeek } from '@/lib/time-queries'
import { isoWeekId } from '@/lib/week'

type TimesheetView = 'week' | 'day'

const VIEW_STORAGE_KEY = 'uren.view'

function initialView(): TimesheetView {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'week' ? 'week' : 'day'
  } catch {
    return 'day'
  }
}

function todayIndex(): number {
  return (new Date().getDay() + 6) % 7
}

export function TimesheetPage() {
  const navigate = useNavigate()
  const meQuery = useMe()
  const [weekId, setWeekId] = useState(() => isoWeekId(new Date()))
  const [view, setView] = useState<TimesheetView>(initialView)
  const [dayIndex, setDayIndex] = useState(todayIndex)
  const [historyOpen, setHistoryOpen] = useState(false)
  const personId = meQuery.data?.person?.id
  const editable = meQuery.isSuccess ? !!personId : true
  // Het teamrooster toont uren van collega's; alleen Beheerder en Partner komen daar.
  const canReadFinance = useCanReadFinance()

  // Een keer op paginaniveau, zodat een net toegevoegde regel het wisselen tussen dag- en
  // weekweergave overleeft.
  const weekQuery = useWeek(weekId, personId)
  const timesheetRows = useTimesheetRows(weekId, weekQuery.data?.rows ?? [], personId)

  function changeView(next: TimesheetView) {
    setView(next)
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next)
    } catch {
      /* private mode */
    }
  }

  const viewControl = (
    <SegmentedControl<TimesheetView>
      segments={[
        { value: 'day', label: 'Dag' },
        { value: 'week', label: 'Week' },
      ]}
      value={view}
      onChange={changeView}
    />
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Uren"
        description="Vul je week in. Klik een cel voor uren, omschrijving en reiskosten."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setHistoryOpen(true)} className="btn-secondary">
              <History className="h-4 w-4" />
              Historie
            </button>
            {canReadFinance ? (
              <SegmentedControl<'mine' | 'team'>
                segments={[
                  { value: 'mine', label: 'Mijn uren' },
                  { value: 'team', label: 'Team' },
                ]}
                value="mine"
                onChange={(value) => {
                  if (value === 'team') navigate({ to: '/uren/team' })
                }}
              />
            ) : null}
          </div>
        }
      />

      <div className="hidden md:block">
        {view === 'week' ? (
          <WeekGrid
            weekId={weekId}
            onWeekChange={setWeekId}
            personId={personId}
            me={meQuery.data}
            editable={editable}
            viewControl={viewControl}
            sheet={timesheetRows}
          />
        ) : (
          <DayView
            weekId={weekId}
            dayIndex={dayIndex}
            onNavigate={(week, day) => {
              setWeekId(week)
              setDayIndex(day)
            }}
            personId={personId}
            me={meQuery.data}
            editable={editable}
            viewControl={viewControl}
            sheet={timesheetRows}
          />
        )}
      </div>
      <div className="md:hidden">
        <DayList
          weekId={weekId}
          dayIndex={dayIndex}
          onNavigate={(week, day) => {
            setWeekId(week)
            setDayIndex(day)
          }}
          personId={personId}
          me={meQuery.data}
          editable={editable}
          sheet={timesheetRows}
        />
      </div>

      <WeekHistoryDrawer
        weekId={weekId}
        personId={personId}
        personName={meQuery.data?.person?.displayName}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  )
}
